import React, { useEffect, useState } from "react";
import {
  Play,
  Plus,
  Sparkles,
  Pencil,
  Trash2,
  Check,
  X,
  Clock3,
} from "lucide-react";
import { api } from "../lib/api.js";
import { useSearchParams } from "react-router-dom";
import { Card, Pill, Modal, Empty, Notice } from "../components/UI.jsx";
const empty = {
    name: "",
    priority: 100,
    matchType: "contains",
    value: "",
    financialType: "expense",
    categoryId: "",
    costCenterId: "",
    projectId: "",
    budgetBucket: "",
    tags: "",
    accountScope: "all",
    accountIds: [],
    isActive: true,
  },
  json = (v) => (typeof v === "string" ? JSON.parse(v) : v || {});
export default function RulesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cfg, setCfg] = useState(null),
    [suggestions, setSuggestions] = useState([]),
    [modal, setModal] = useState(false),
    [editing, setEditing] = useState(null),
    [msg, setMsg] = useState(""),
    [err, setErr] = useState(""),
    [deciding, setDeciding] = useState(null),
    [f, setF] = useState(empty);
  const load = () =>
    Promise.all([api("/config"), api("/rule-suggestions")]).then(([c, s]) => {
      setCfg(c);
      setSuggestions(s);
    });
  useEffect(() => {
    load().catch((e) => setErr(e.message));
  }, []);
  useEffect(() => {
    if (!cfg || searchParams.get("new") !== "1") return;
    const pattern = searchParams.get("pattern") || "",
      kind = searchParams.get("kind"),
      direction = searchParams.get("direction");
    setEditing(null);
    setF({
      ...empty,
      name: `${pattern || "Movement"} ${kind === "transfer" ? "transfer" : "classification"}`,
      value: pattern,
      financialType:
        kind === "transfer"
          ? "transfer"
          : direction === "income"
            ? "active_income"
            : "expense",
    });
    setModal(true);
    setSearchParams({}, { replace: true });
  }, [cfg, searchParams, setSearchParams]);
  const openEdit = (r) => {
    const c = json(r.conditions_json),
      a = json(r.actions_json);
    setEditing(r);
    setF({
      name: r.name,
      priority: r.priority,
      matchType: c.matchType || "contains",
      value: c.regex || c.value || "",
      financialType: a.financialType || "expense",
      categoryId: a.categoryId ? String(a.categoryId) : "",
      costCenterId: a.costCenterId ? String(a.costCenterId) : "",
      projectId: a.projectId ? String(a.projectId) : "",
      budgetBucket: a.budgetBucket || "",
      tags: (a.tags || []).join(", "),
      accountScope:
        Array.isArray(c.accountIds) && c.accountIds.length
          ? "selected"
          : "all",
      accountIds: Array.isArray(c.accountIds)
        ? c.accountIds.map(String)
        : c.accountId
          ? [String(c.accountId)]
          : [],
      isActive: r.is_active !== 0,
    });
    setModal(true);
  };
  const body = () => ({
    name: f.name,
    priority: f.priority,
    isActive: f.isActive,
    conditions: {
      ...(f.matchType === "regex"
        ? { matchType: "regex", regex: f.value }
        : { matchType: f.matchType, value: f.value }),
      ...(f.accountScope === "selected"
        ? { accountIds: f.accountIds.map(Number) }
        : {}),
    },
    actions: {
      financialType: f.financialType,
      categoryId: f.categoryId || null,
      costCenterId: f.costCenterId || null,
      projectId: f.projectId || null,
      budgetBucket: f.budgetBucket || null,
      tags: f.tags
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean),
    },
  });
  const save = async (e) => {
    e.preventDefault();
    if (f.accountScope === "selected" && !f.accountIds.length) {
      setErr("Select at least one account, or choose All accounts.");
      return;
    }
    try {
      await api(editing ? `/rules/${editing.id}` : "/rules", {
        method: editing ? "PUT" : "POST",
        body: body(),
      });
      setModal(false);
      setEditing(null);
      await load();
    } catch (e) {
      setErr(e.message);
    }
  };
  const remove = async (r) => {
    if (
      !confirm(
        `Delete rule “${r.name}”? Existing movements will not be changed.`,
      )
    )
      return;
    await api(`/rules/${r.id}`, { method: "DELETE" });
    setMsg("Rule deleted. Previous classifications were kept.");
    load();
  };
  const rerun = async () => {
    const r = await api("/rules/rerun", { method: "POST", body: {} });
    setMsg(`Rules re-applied to ${r.updated} movements.`);
  };
  const generate = async () => {
    const r = await api("/rule-suggestions/generate", {
      method: "POST",
      body: {},
    });
    setMsg(`${r.added} new private rule suggestions found.`);
    load();
  };
  const decide = async (s, status) => {
    try {
      setErr(""); setDeciding(`${s.id}:${status}`);
      await api(`/rule-suggestions/${s.id}`, { method: "PUT", body: { status } });
      setMsg(status === "approved" ? "Rule approved and added." : status === "rejected" ? "Suggestion rejected." : "Saved for later.");
      await load();
    } catch (e) { setErr(e.message); }
    finally { setDeciding(null); }
  };
  if (!cfg) return <div className="loading">Loading rules…</div>;
  const proposed = suggestions.filter(
    (x) => x.status === "proposed" || x.status === "later",
  );
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Automatic classification</h1>
          <p>
            Pisto learns privately from patterns you confirm. Your statements
            never leave this installation.
          </p>
        </div>
        <div className="actions">
          <button className="secondary" onClick={generate}>
            <Sparkles size={16} />
            Find suggestions
          </button>
          <button className="secondary" onClick={rerun}>
            <Play size={16} />
            Re-run rules
          </button>
          <button
            className="primary"
            onClick={() => {
              setEditing(null);
              setF({ ...empty });
              setModal(true);
            }}
          >
            <Plus size={16} />
            New rule
          </button>
        </div>
      </div>
      {msg && <Notice tone="success">{msg}</Notice>}
      {err && <Notice tone="error">{err}</Notice>}
      <Card>
        <div className="section-head">
          <div>
            <Sparkles />
            <h2>Proposed rule gallery</h2>
          </div>
          <Pill>{proposed.length} to review</Pill>
        </div>
        {!proposed.length ? (
          <Empty
            title="No proposed rules"
            text="Find suggestions after classifying several similar movements."
          />
        ) : (
          <div className="rule-list">
            {proposed.map((s) => (
              <div className="rule" key={s.id}>
                <div className="rule-order">
                  {Math.round(Number(s.confidence))}%
                </div>
                <div>
                  <b>{s.name}</b>
                  <small>{s.explanation}</small>
                  <small><b>When:</b> {s.conditions?.matchType || "contains"} “{s.conditions?.value || s.conditions?.regex || ""}”</small>
                  {(s.actionSummary||[]).length ? <div className="pills">{s.actionSummary.map(action=><Pill key={action}>{action}</Pill>)}</div> : <small className="error"><b>No action defined.</b> This suggestion cannot be approved until it says what will change.</small>}
                  <small>{s.example_count} matching examples</small>
                </div>
                <div className="row-actions">
                  <button
                    className="primary compact"
                    disabled={Boolean(deciding) || !s.canApprove}
                    onClick={() => decide(s, "approved")}
                  >
                    <Check size={14} />
                    Approve
                  </button>
                  <button
                    className="secondary compact"
                    disabled={Boolean(deciding)}
                    onClick={() => decide(s, "later")}
                  >
                    <Clock3 size={14} />
                    Later
                  </button>
                  <button
                    className="secondary compact"
                    disabled={Boolean(deciding)}
                    onClick={() => decide(s, "rejected")}
                  >
                    <X size={14} />
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card>
        <h2>Approved rules</h2>
        {!cfg.rules.length ? (
          <Empty />
        ) : (
          <div className="rule-list">
            {cfg.rules.map((r) => {
              const c = json(r.conditions_json),
                a = json(r.actions_json);
              return (
                <div className="rule" key={r.id}>
                  <div className="rule-order">{r.priority}</div>
                  <div>
                    <b>{r.name}</b>
                    <small>
                      {c.regex
                        ? `matches /${c.regex}/`
                        : `${c.matchType || "contains"} “${c.value || ""}”`}
                    </small>
                  </div>
                  <div className="pills">
                    <Pill tone={r.is_active ? "green" : ""}>
                      {r.is_active ? "active" : "paused"}
                    </Pill>
                    <Pill>{a.financialType || "classify"}</Pill>
                    <Pill>
                      {Array.isArray(c.accountIds) && c.accountIds.length
                        ? `${c.accountIds.length} account${c.accountIds.length === 1 ? "" : "s"}`
                        : "All accounts"}
                    </Pill>
                    <span className="hits">
                      <Sparkles size={14} />
                      {r.hit_count} hits
                    </span>
                  </div>
                  <span className="row-actions">
                    <button className="icon" onClick={() => openEdit(r)}>
                      <Pencil size={15} />
                    </button>
                    <button className="icon" onClick={() => remove(r)}>
                      <Trash2 size={15} />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
      {modal && (
        <Modal
          title={editing ? "Edit automatic rule" : "Create automatic rule"}
          screenId="D0401"
          onClose={() => setModal(false)}
        >
          <form onSubmit={save} className="form-grid">
            <label>
              Rule name
              <input
                required
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
              />
            </label>
            <label>
              Priority
              <input
                type="number"
                value={f.priority}
                onChange={(e) => setF({ ...f, priority: e.target.value })}
              />
            </label>
            <label>
              Match
              <select
                value={f.matchType}
                onChange={(e) => setF({ ...f, matchType: e.target.value })}
              >
                <option value="contains">Description contains</option>
                <option value="starts_with">Starts with</option>
                <option value="exact">Exact description</option>
                <option value="regex">Advanced pattern</option>
              </select>
            </label>
            <label>
              Text / pattern
              <input
                required
                value={f.value}
                onChange={(e) => setF({ ...f, value: e.target.value })}
              />
            </label>
            <fieldset className="span2 rule-account-scope">
              <legend>Apply this rule to</legend>
              <div className="period-choices">
                <button
                  type="button"
                  className={f.accountScope === "all" ? "active" : ""}
                  onClick={() =>
                    setF({ ...f, accountScope: "all", accountIds: [] })
                  }
                >
                  All accounts
                </button>
                <button
                  type="button"
                  className={f.accountScope === "selected" ? "active" : ""}
                  onClick={() => setF({ ...f, accountScope: "selected" })}
                >
                  Selected accounts
                </button>
              </div>
              {f.accountScope === "selected" && (
                <div className="account-choice-grid">
                  {(cfg.accounts || []).map((account) => {
                    const id = String(account.id),
                      checked = f.accountIds.includes(id);
                    return (
                      <label className="check" key={account.id}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setF({
                              ...f,
                              accountIds: checked
                                ? f.accountIds.filter((x) => x !== id)
                                : [...f.accountIds, id],
                            })
                          }
                        />
                        {account.name} · {account.currency}
                      </label>
                    );
                  })}
                </div>
              )}
            </fieldset>
            <label>
              Financial type
              <select
                value={f.financialType}
                onChange={(e) => setF({ ...f, financialType: e.target.value })}
              >
                {[
                  "active_income",
                  "passive_income",
                  "expense",
                  "investment",
                  "transfer",
                  "excluded",
                ].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label>
              Category
              <select
                value={f.categoryId}
                onChange={(e) => setF({ ...f, categoryId: e.target.value })}
              >
                <option value="">No change</option>
                {cfg.categories.map((c) => (
                  <option value={c.id} key={c.id}>
                    {c.custom_name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Cost center
              <select
                value={f.costCenterId}
                onChange={(e) => setF({ ...f, costCenterId: e.target.value })}
              >
                <option value="">No change</option>
                {cfg.costCenters.map((c) => (
                  <option value={c.id} key={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Project
              <select
                value={f.projectId}
                onChange={(e) => setF({ ...f, projectId: e.target.value })}
              >
                <option value="">No change</option>
                {(cfg.projects || []).map((p) => (
                  <option value={p.id} key={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              50 / 30 / 20
              <select
                value={f.budgetBucket}
                onChange={(e) => setF({ ...f, budgetBucket: e.target.value })}
              >
                <option value="">No change</option>
                {["needs", "wants", "goals", "unclassified"].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label>
              Tags
              <input
                value={f.tags}
                onChange={(e) => setF({ ...f, tags: e.target.value })}
              />
            </label>
            {editing && (
              <label className="check span2">
                <input
                  type="checkbox"
                  checked={f.isActive}
                  onChange={(e) => setF({ ...f, isActive: e.target.checked })}
                />
                Rule active
              </label>
            )}
            <button className="primary span2">
              {editing ? "Save rule" : "Create rule"}
            </button>
          </form>
        </Modal>
      )}
    </>
  );
}
